export interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  title: string;
  description: string;
  permission?: string;
  requestBody?: string;
  responseExample?: string;
  pathParams?: { name: string; type: string; description: string }[];
}

export interface ApiModule {
  id: string;
  name: string;
  description: string;
  baseUrl: string;
  endpoints: ApiEndpoint[];
}

export const apiModules: ApiModule[] = [
  {
    id: 'materials',
    name: 'Materiales',
    description:
      'Gestión de materiales del catálogo con partidas arancelarias e historial de tarifas.',
    baseUrl: '/api/materials',
    endpoints: [
      {
        method: 'GET',
        path: '/api/materials',
        title: 'Listar materiales',
        description:
          'Obtiene todos los materiales activos del tenant, incluyendo la tarifa arancelaria vigente y la línea de negocio (LOB) asociada.',
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                code: 'HPE-DL380',
                externalCode: 'P55246-B21',
                name: 'HPE ProLiant DL380 Gen11',
                description: 'Servidor rack 2U',
                tariffHeading: '8471.49.00',
                isActive: true,
                tenantId: 'uuid',
                lobId: 'uuid',
                lob: { id: 'uuid', name: 'Infraestructura TI' },
                activeTariffRate: 5.0,
              },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/materials/:id',
        title: 'Obtener material por ID',
        description:
          'Obtiene un material específico con todo su historial de tarifas arancelarias.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del material' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'uuid',
              code: 'HPE-DL380',
              name: 'HPE ProLiant DL380 Gen11',
              tariffHistory: [
                {
                  id: 'uuid',
                  rate: 5.0,
                  isActive: true,
                  validFrom: '2026-01-01T00:00:00Z',
                  validTo: null,
                },
              ],
              activeTariffRate: 5.0,
            },
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/materials',
        title: 'Crear material',
        description:
          'Crea un nuevo material en el catálogo. Si se proporciona tariffRate, se crea el primer registro en el historial de tarifas.',
        permission: 'manage_master_data',
        requestBody: JSON.stringify(
          {
            code: 'DELL-R760',
            externalCode: '210-BKMW',
            name: 'Dell PowerEdge R760',
            description: 'Servidor rack 2U Intel Xeon',
            tariffHeading: '8471.49.00',
            tariffRate: 5.0,
            lobId: 'uuid-de-lob (opcional)',
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'nuevo-uuid',
              code: 'DELL-R760',
              name: 'Dell PowerEdge R760',
              activeTariffRate: 5.0,
            },
          },
          null,
          2
        ),
      },
      {
        method: 'PUT',
        path: '/api/materials/:id',
        title: 'Actualizar material',
        description:
          'Actualiza los datos de un material existente. Si se envía un tariffRate diferente al actual, se crea un nuevo registro en el historial.',
        permission: 'manage_master_data',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del material' }],
        requestBody: JSON.stringify(
          {
            code: 'DELL-R760',
            name: 'Dell PowerEdge R760 (actualizado)',
            description: 'Servidor rack 2U Intel Xeon 5th Gen',
            tariffHeading: '8471.49.00',
            tariffRate: 7.5,
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: { id: 'uuid', code: 'DELL-R760', activeTariffRate: 7.5 },
          },
          null,
          2
        ),
      },
      {
        method: 'DELETE',
        path: '/api/materials/:id',
        title: 'Eliminar material',
        description:
          'Realiza un soft delete del material (isActive=false). No se elimina físicamente de la base de datos.',
        permission: 'manage_master_data',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del material' }],
        responseExample: JSON.stringify(
          {
            success: true,
            message: 'Material eliminado',
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/materials/:id/tariff',
        title: 'Agregar tarifa arancelaria',
        description:
          'Agrega un nuevo registro de tarifa al historial del material. El registro anterior se desactiva automáticamente.',
        permission: 'manage_master_data',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del material' }],
        requestBody: JSON.stringify(
          {
            rate: 10.0,
            validFrom: '2026-03-01T00:00:00Z',
            notes: 'Actualización por decreto supremo',
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'uuid',
              code: 'HPE-DL380',
              activeTariffRate: 10.0,
              tariffHistory: [
                { rate: 10.0, isActive: true, validFrom: '2026-03-01T00:00:00Z' },
                { rate: 5.0, isActive: false, validFrom: '2026-01-01T00:00:00Z' },
              ],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/materials/:id/tariff-history',
        title: 'Historial de tarifas',
        description:
          'Obtiene el historial completo de tarifas arancelarias de un material, ordenado por fecha de creación descendente.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del material' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                rate: 10.0,
                isActive: true,
                validFrom: '2026-03-01T00:00:00Z',
                validTo: null,
                notes: 'Actualización',
              },
              {
                id: 'uuid',
                rate: 5.0,
                isActive: false,
                validFrom: '2026-01-01T00:00:00Z',
                validTo: '2026-03-01T00:00:00Z',
                notes: null,
              },
            ],
          },
          null,
          2
        ),
      },
    ],
  },
  {
    id: 'quotes',
    name: 'Cotizaciones',
    description:
      'Creación, gestión y flujo de aprobación de cotizaciones comerciales multi-sección.',
    baseUrl: '/api/quotes',
    endpoints: [
      {
        method: 'GET',
        path: '/api/quotes',
        title: 'Listar cotizaciones',
        description:
          'Obtiene todas las cotizaciones del tenant. Los vendedores (SALESPERSON) solo ven sus propias cotizaciones. Admins y aprobadores ven todas.',
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                code: 'QT-2026-0001',
                clientName: 'Banco Nacional de Bolivia',
                clientEmail: 'compras@bnb.com.bo',
                status: 'PENDING_APPROVAL',
                baseCost: 45000,
                finalPrice: 58500,
                actualMargin: 23.08,
                currency: { code: 'USD', name: 'Dólar Estadounidense' },
                createdBy: { firstName: 'Carla', lastName: 'Rojas' },
              },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/quotes/:id',
        title: 'Obtener cotización por ID',
        description:
          'Obtiene una cotización con todos sus detalles: secciones, line items, materiales, impuestos, escenarios de pago, aprobaciones y modelo de pricing.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID de la cotización' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'uuid',
              code: 'QT-2026-0001',
              clientName: 'Banco Nacional de Bolivia',
              status: 'PENDING_APPROVAL',
              sections: [
                {
                  modelId: 'uuid',
                  model: { name: 'Cotización de Equipamiento' },
                  lineItems: [
                    {
                      material: { code: 'HPE-DL380', name: 'HPE ProLiant DL380' },
                      quantity: 3,
                      unitPrice: 15000,
                      totalPrice: 45000,
                    },
                  ],
                },
              ],
              taxes: [{ taxName: 'IVA', taxRate: 13, taxAmount: 7605 }],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/quotes',
        title: 'Crear cotización',
        description:
          'Crea una nueva cotización. Genera automáticamente el código (QT-YYYY-XXXX). Si el margen está por debajo del floor del modelo, se envía a aprobación automáticamente.',
        requestBody: JSON.stringify(
          {
            clientName: 'YPFB Corporación',
            clientEmail: 'compras@ypfb.gob.bo',
            clientPhone: '+591 2 2156000',
            currencyId: 'uuid-moneda',
            lobId: 'uuid-lob',
            countryId: 'uuid-pais',
            baseCost: 120000,
            targetMargin: 25,
            suggestedPrice: 160000,
            finalPrice: 155000,
            actualMargin: 22.58,
            validityDays: 30,
            validUntil: '2026-04-01T00:00:00Z',
            sections: [
              {
                modelId: 'uuid-modelo',
                order: 0,
                globalInputs: { MARGEN_OBJETIVO: 25 },
                lineItems: [
                  {
                    materialId: 'uuid-material',
                    lineNumber: 0,
                    quantity: 5,
                    costInputs: {},
                    unitPrice: 24000,
                    totalPrice: 120000,
                  },
                ],
              },
            ],
            taxes: [{ taxName: 'IVA', taxRate: 13, taxAmount: 20150 }],
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: { id: 'nuevo-uuid', code: 'QT-2026-0005', status: 'PENDING_APPROVAL' },
          },
          null,
          2
        ),
      },
      {
        method: 'PUT',
        path: '/api/quotes/:id',
        title: 'Actualizar cotización',
        description:
          'Actualiza una cotización existente. Solo se pueden editar cotizaciones en estado DRAFT o ADJUSTMENT_REQUESTED, y solo por su creador.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID de la cotización' }],
        requestBody: JSON.stringify(
          {
            clientName: 'YPFB Corporación',
            clientEmail: 'compras@ypfb.gob.bo',
            currencyId: 'uuid-moneda',
            baseCost: 130000,
            targetMargin: 25,
            suggestedPrice: 173333,
            finalPrice: 168000,
            actualMargin: 22.62,
            validityDays: 30,
            validUntil: '2026-04-01T00:00:00Z',
            sections: [],
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: { id: 'uuid', code: 'QT-2026-0005', status: 'DRAFT' },
          },
          null,
          2
        ),
      },
      {
        method: 'DELETE',
        path: '/api/quotes/:id',
        title: 'Eliminar cotización',
        description:
          'Elimina una cotización permanentemente. Solo se pueden eliminar cotizaciones en estado DRAFT que no estén congeladas, y solo por su creador.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID de la cotización' }],
        responseExample: JSON.stringify(
          {
            success: true,
            message: 'Cotización eliminada',
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/quotes/:id/resubmit',
        title: 'Reenviar cotización',
        description:
          'Reenvía una cotización que tuvo ajuste solicitado. Cambia el estado a PENDING_APPROVAL y crea una nueva solicitud de aprobación. Solo funciona para cotizaciones en ADJUSTMENT_REQUESTED.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID de la cotización' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: { id: 'uuid', code: 'QT-2026-0005', status: 'PENDING_APPROVAL' },
          },
          null,
          2
        ),
      },
    ],
  },
  {
    id: 'models',
    name: 'Modelos de Pricing',
    description:
      'Modelos de pricing con variables (NUMBER, PERCENTAGE, CALCULATED, ENUM, TIERED, LOOKUP), tiers de precios y tablas de lookup.',
    baseUrl: '/api/models',
    endpoints: [
      {
        method: 'GET',
        path: '/api/models',
        title: 'Listar modelos',
        description:
          'Obtiene todos los modelos de pricing activos del tenant, incluyendo sus variables (con tiers y lookup tables) y conteo de cotizaciones asociadas.',
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                name: 'Cotización de Equipamiento',
                description: 'Modelo para cotizar equipos de TI',
                targetMarginPercentage: 25,
                floorMarginPercentage: 15,
                variables: [
                  { code: 'COSTO_EQUIPO', name: 'Costo del Equipo', variableType: 'CURRENCY' },
                  { code: 'MARGEN', name: 'Margen', variableType: 'PERCENTAGE' },
                ],
                _count: { quotes: 5 },
              },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/models/:id',
        title: 'Obtener modelo por ID',
        description:
          'Obtiene un modelo con todas sus variables, tiers de precios y tablas de lookup.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'uuid',
              name: 'Cotización de Equipamiento',
              targetMarginPercentage: 25,
              floorMarginPercentage: 15,
              variables: [
                {
                  code: 'COSTO_EQUIPO',
                  name: 'Costo del Equipo',
                  variableType: 'CURRENCY',
                  isRequired: true,
                  isGlobal: false,
                  tiers: [],
                },
              ],
              lookupTables: [],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/models',
        title: 'Crear modelo',
        description:
          'Crea un nuevo modelo de pricing vacío (sin variables). Las variables se agregan después con el endpoint POST /api/models/:id/variables.',
        permission: 'manage_models',
        requestBody: JSON.stringify(
          {
            name: 'Servicios Profesionales',
            description: 'Modelo para cotizar servicios de consultoría',
            targetMarginPercentage: 30,
            floorMarginPercentage: 20,
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'nuevo-uuid',
              name: 'Servicios Profesionales',
              variables: [],
              lookupTables: [],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'PUT',
        path: '/api/models/:id',
        title: 'Actualizar modelo',
        description:
          'Actualiza los datos generales del modelo (nombre, descripción, márgenes). No modifica las variables.',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        requestBody: JSON.stringify(
          {
            name: 'Servicios Profesionales v2',
            description: 'Modelo actualizado',
            targetMarginPercentage: 35,
            floorMarginPercentage: 22,
          },
          null,
          2
        ),
      },
      {
        method: 'DELETE',
        path: '/api/models/:id',
        title: 'Eliminar modelo',
        description:
          'Soft delete del modelo (isActive=false). Las cotizaciones existentes conservan sus snapshots.',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        responseExample: JSON.stringify({ success: true, message: 'Modelo eliminado' }, null, 2),
      },
      {
        method: 'POST',
        path: '/api/models/:id/clone',
        title: 'Clonar modelo',
        description:
          'Crea una copia completa del modelo incluyendo todas sus variables, tiers de precios y tablas de lookup. El nombre se sufija con "(Copia)".',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo a clonar' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: { id: 'nuevo-uuid', name: 'Cotización de Equipamiento (Copia)' },
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/models/:id/variables',
        title: 'Listar variables',
        description:
          'Obtiene todas las variables del modelo ordenadas por campo order. Incluye tiers y referencia a lookup table.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                code: 'COSTO_EQUIPO',
                name: 'Costo del Equipo',
                variableType: 'CURRENCY',
                order: 0,
                isRequired: true,
                isGlobal: false,
                tiers: [],
              },
              {
                id: 'uuid',
                code: 'PRECIO_VENTA',
                name: 'Precio de Venta',
                variableType: 'CALCULATED',
                formula: 'COSTO_EQUIPO * (1 + MARGEN / 100)',
                order: 1,
              },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/models/:id/variables',
        title: 'Crear variable',
        description:
          'Agrega una nueva variable al modelo. Tipos soportados: NUMBER, PERCENTAGE, CURRENCY, TEXT, BOOLEAN, CALCULATED, ENUM, TIERED, LOOKUP. Las variables CALCULATED usan fórmulas que referencian otras variables por su código.',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        requestBody: JSON.stringify(
          {
            code: 'DESCUENTO_VOLUMEN',
            name: 'Descuento por Volumen',
            variableType: 'TIERED',
            description: 'Descuento escalonado según cantidad',
            order: 5,
            isRequired: true,
            isGlobal: false,
            tiers: [
              { minQuantity: 1, maxQuantity: 10, discount: 0, order: 0 },
              { minQuantity: 11, maxQuantity: 50, discount: 5, order: 1 },
              { minQuantity: 51, maxQuantity: null, discount: 10, order: 2 },
            ],
          },
          null,
          2
        ),
        responseExample: JSON.stringify(
          {
            success: true,
            data: {
              id: 'nuevo-uuid',
              code: 'DESCUENTO_VOLUMEN',
              variableType: 'TIERED',
              tiers: ['...'],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'PUT',
        path: '/api/models/:modelId/variables/:variableId',
        title: 'Actualizar variable',
        description:
          'Actualiza una variable existente. Si se proporcionan tiers, reemplaza los existentes. Valida fórmulas CALCULATED y detecta dependencias circulares.',
        permission: 'manage_models',
        pathParams: [
          { name: 'modelId', type: 'UUID', description: 'ID del modelo' },
          { name: 'variableId', type: 'UUID', description: 'ID de la variable' },
        ],
        requestBody: JSON.stringify(
          {
            code: 'DESCUENTO_VOLUMEN',
            name: 'Descuento por Volumen (actualizado)',
            variableType: 'TIERED',
            tiers: [
              { minQuantity: 1, maxQuantity: 5, discount: 0, order: 0 },
              { minQuantity: 6, maxQuantity: 20, discount: 8, order: 1 },
              { minQuantity: 21, maxQuantity: null, discount: 15, order: 2 },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'DELETE',
        path: '/api/models/:modelId/variables/:variableId',
        title: 'Eliminar variable',
        description: 'Elimina permanentemente una variable del modelo.',
        permission: 'manage_models',
        pathParams: [
          { name: 'modelId', type: 'UUID', description: 'ID del modelo' },
          { name: 'variableId', type: 'UUID', description: 'ID de la variable' },
        ],
        responseExample: JSON.stringify({ success: true, message: 'Variable eliminada' }, null, 2),
      },
      {
        method: 'POST',
        path: '/api/models/:id/variables/reorder',
        title: 'Reordenar variables',
        description:
          'Establece el orden de las variables del modelo. Se envía un array con los IDs en el orden deseado.',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        requestBody: JSON.stringify(
          {
            variableIds: ['uuid-var-1', 'uuid-var-2', 'uuid-var-3'],
          },
          null,
          2
        ),
      },
      {
        method: 'GET',
        path: '/api/models/:id/lookup-tables',
        title: 'Listar lookup tables',
        description:
          'Obtiene todas las tablas de lookup de un modelo. Las lookup tables son matrices bidimensionales usadas por variables tipo LOOKUP.',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        responseExample: JSON.stringify(
          {
            success: true,
            data: [
              {
                id: 'uuid',
                name: 'Tabla de Descuentos',
                code: 'TABLA_DESC',
                rowDimension: 'Categoría',
                colDimension: 'Volumen',
                data: {
                  rows: ['Servidores', 'Storage', 'Networking'],
                  cols: ['1-10', '11-50', '50+'],
                  values: [
                    [0, 5, 10],
                    [0, 3, 8],
                    [0, 7, 12],
                  ],
                },
              },
            ],
          },
          null,
          2
        ),
      },
      {
        method: 'POST',
        path: '/api/models/:id/lookup-tables',
        title: 'Crear lookup table',
        description:
          'Crea una nueva tabla de lookup para el modelo. Las tablas tienen dos dimensiones (filas y columnas) y una matriz de valores numéricos.',
        permission: 'manage_models',
        pathParams: [{ name: 'id', type: 'UUID', description: 'ID del modelo' }],
        requestBody: JSON.stringify(
          {
            name: 'Tabla de Descuentos',
            code: 'TABLA_DESC',
            rowDimension: 'Categoría',
            colDimension: 'Volumen',
            data: {
              rows: ['Servidores', 'Storage'],
              cols: ['1-10', '11-50'],
              values: [
                [0, 5],
                [0, 3],
              ],
            },
          },
          null,
          2
        ),
      },
      {
        method: 'PUT',
        path: '/api/models/:id/lookup-tables/:tableId',
        title: 'Actualizar lookup table',
        description: 'Actualiza una tabla de lookup existente (nombre, dimensiones, datos).',
        permission: 'manage_models',
        pathParams: [
          { name: 'id', type: 'UUID', description: 'ID del modelo' },
          { name: 'tableId', type: 'UUID', description: 'ID de la lookup table' },
        ],
      },
      {
        method: 'DELETE',
        path: '/api/models/:id/lookup-tables/:tableId',
        title: 'Eliminar lookup table',
        description: 'Elimina permanentemente una tabla de lookup del modelo.',
        permission: 'manage_models',
        pathParams: [
          { name: 'id', type: 'UUID', description: 'ID del modelo' },
          { name: 'tableId', type: 'UUID', description: 'ID de la lookup table' },
        ],
        responseExample: JSON.stringify(
          { success: true, message: 'Tabla de lookup eliminada' },
          null,
          2
        ),
      },
    ],
  },
];
